import { type FC, useMemo } from "react";

import { t } from "i18n/i18n";
import { type ClickEvent, Menu, MenuHeader, MenuItem } from "mds/components/Menu";
import { SquareButton } from "mds/components/buttons/SquareButton";
import { ChevronDownIcon, PersonGroupIcon, PersonSingleIcon, ViewPersonIcon } from "mds/icons";
import { classUserSelectors } from "store/selectors";
import { selectClassUserIdsPresentInClass } from "store/selectors/pubsub-virtual-class-state";
import type { NestedPageGroupType } from "store/selectors/types";
import { useListSelector } from "store/store-hooks";
import { compactMap } from "utils/collections";
import { usePinParticipantTools } from "utils/hooks/use-virtual-participant-tools";

type GroupMenuOption = {
  id: string;
  name: string;
  presentCount: number;
  presentClassUserIds: string[];
};

interface ViewGroupMenuProps {
  pageGroups: NestedPageGroupType[];
}

export const ViewGroupMenu: FC<ViewGroupMenuProps> = ({ pageGroups }) => {
  const classUsers = useListSelector(classUserSelectors.filterForCurrentSection);
  const presentClassUserIds = useListSelector(selectClassUserIdsPresentInClass);
  const { onPinClassUserIdList } = usePinParticipantTools();
  const classUserIdByUserId = useMemo(
    () => new Map(classUsers.map((classUser) => [classUser.user_id, classUser.id])),
    [classUsers],
  );

  const presentClassUserIdSet = useMemo(() => new Set(presentClassUserIds), [presentClassUserIds]);

  const groupOptions: GroupMenuOption[] = useMemo(
    () =>
      pageGroups.map((group, index) => {
        const presentClassUserIdsForGroup = compactMap(group.page_group_users, (pageGroupUser) => {
          if (!pageGroupUser.is_ready) {
            return null;
          }
          const classUserId = classUserIdByUserId.get(pageGroupUser.user_id);
          if (classUserId == null || !presentClassUserIdSet.has(classUserId)) {
            return null;
          }
          return classUserId;
        });

        return {
          id: group.id,
          name: t("common.group_title", { number: index + 1 }),
          presentCount: presentClassUserIdsForGroup.length,
          presentClassUserIds: presentClassUserIdsForGroup,
        };
      }),
    [pageGroups, classUserIdByUserId, presentClassUserIdSet],
  );

  const buttonLabel = t("group_configuration_card.view_group");

  const menuButton = (
    <SquareButton aria-label={buttonLabel} kind="tertiary" size="s" title={buttonLabel}>
      <ViewPersonIcon />
      {buttonLabel}
      <ChevronDownIcon />
    </SquareButton>
  );

  const onItemClick = (event: ClickEvent) => {
    const option = event.value as GroupMenuOption;
    onPinClassUserIdList(option.presentClassUserIds);
  };

  return (
    <Menu align="end" menuButton={menuButton}>
      <MenuHeader className="text-xs text-black-tint-55">
        {t("group_configuration_card.select_group_to_view_participants")}
      </MenuHeader>
      {groupOptions.map((group) => (
        <MenuItem key={group.id} value={group} onClick={onItemClick}>
          <div className="flex w-full min-w-[230px] items-center gap-2">
            <div className="flex h-6 w-6 flex-none items-center justify-center rounded-full bg-hibiscus-tint-95">
              <PersonGroupIcon className="text-hibiscus" size="xs" />
            </div>
            <span className="body-s min-w-0 flex-1 truncate">{group.name}</span>
            <span className="flex w-10 flex-none items-center gap-1 text-black-tint-40">
              <PersonSingleIcon className="flex-none" />
              <span className="body-s w-[2ch] flex-none text-right tabular-nums">
                {group.presentCount}
              </span>
            </span>
          </div>
        </MenuItem>
      ))}
    </Menu>
  );
};
